import { useEffect, useState } from "react"
import { DragDropContext, Draggable, Droppable, DropResult } from "react-beautiful-dnd"
import { useTranslation } from "react-i18next"
import { useAppDispatch, useAppSelector } from "@/core/root-reducer"
import { filterFetch } from "@/redux/filter.actions"
import { surveyFetch } from "@/redux/survey.actions"
import { fetchFilters } from "@/redux/_archive.actions"
import { DashboardFilters } from "@/redux/filter.types"
import Dropdown from "@/components/dropdown"
import Modal from "@/components/modal"
import PageLoader from "@/components/page-loader"
import Button from "@/components/button"
import { STATUS_LOADED, STATUS_LOADING, STATUS_SAVING } from "@/redux/_status.types"

interface Props {
  title?: string
  help?: string
  example?: string
  isOrdered?: boolean //Account filters only, the selected ones are numbered and reordered by drag and drop
  selectedNames: string[]
  onClose: () => void
  onSave: (names: string[]) => Promise<boolean>
}

//Filter names picked from the account filters or a survey (role scope, manager filters, hidden filters)
export default function SelectFilterNamesModal({ title, help, example, isOrdered, selectedNames, onClose, onSave }: Props) {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const color = useAppSelector((state) => state._session.accountColors.active)
  const [selected, setSelected] = useState(selectedNames)
  const [names, setNames] = useState<string[]>([])
  const [status, setStatus] = useState("loading")
  const [attempt, setAttempt] = useState(0)
  const [saving, setSaving] = useState(false)
  const [surveys, setSurveys] = useState<{ id: string; name: string }[]>([])
  const [surveyId, setSurveyId] = useState("")
  const [surveysStatus, setSurveysStatus] = useState("loading")
  const sources = [{ id: "", name: t("filters_custom") }, ...surveys]

  useEffect(() => {
    if (isOrdered) return
    let cancelled = false
    setSurveysStatus("loading")
    async function loadSurveys() {
      const response: any = await dispatch(surveyFetch())
      if (cancelled) return
      if (!Array.isArray(response)) setSurveysStatus("error")
      else {
        setSurveys(response.map(({ id, name }) => ({ id, name })))
        setSurveysStatus("loaded")
      }
    }
    loadSurveys()
    return () => { cancelled = true }
  }, [dispatch, attempt, isOrdered])

  useEffect(() => {
    let cancelled = false
    setStatus("loading")
    async function load() {
      const response: any = await dispatch(surveyId
        ? fetchFilters(new DashboardFilters(), surveyId)
        : filterFetch(false, false))
      if (cancelled) return
      if (response.error || !Array.isArray(response)) setStatus("error")
      else {
        setNames(Array.from(new Set<string>(response.map((filter) => filter.name))))
        setStatus("loaded")
      }
    }
    load()
    return () => { cancelled = true }
  }, [dispatch, surveyId, attempt])

  function reorder(result: DropResult) {
    if (!result.destination) return
    const reordered = [...selected]
    const [moved] = reordered.splice(result.source.index, 1)
    reordered.splice(result.destination.index, 0, moved)
    setSelected(reordered)
  }

  //Meme rendu que les filtres du tableau de bord, actif ou non
  function renderItem(name: string, rank?: number) {
    const isActive = selected.includes(name)
    return (
      <div
        key={name}
        className={"filters-item flex items-center" + (isActive ? " filters-item-active" : " filters-item-activable")}
        style={{ color: isActive ? color : "" }}
        onClick={() => { if (!saving) setSelected((current) => current.includes(name) ? current.filter((item) => item !== name) : [...current, name]) }}>
        {rank && (
          <span
            className="flex items-center justify-center"
            style={{ backgroundColor: color, color: "white", borderRadius: "50%", width: 20, height: 20, fontSize: 12, marginRight: 8 }}>
            {rank}
          </span>
        )}
        {name}
      </div>
    )
  }

  async function save() {
    if (saving || status !== "loaded") return
    setSaving(true)
    try {
      if (await onSave(selected)) onClose()
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal title={title ?? t("roles_dashboard_scope")} onClose={() => { if (!saving) onClose() }}
      isLarge
      // Le menu de la dropdown deborde de la zone de contenu : sans ca, la
      // modale affiche une barre de defilement horizontale et verticale.
      disableOverflow
      isCloseButtonVisible
      onNext={save} onNextCta={t("utils_save")} nextDisabled={saving || status !== "loaded"}
      status={saving ? STATUS_SAVING : undefined}>
      {/** A gauche le texte d'explication et les filtres juste dessous, a droite la source des filtres. */}
      <div className="flex gap-4 items-start">
        <div className="flex1">
          <p>{help ?? t("roles_dashboard_scope_select")}</p>
          {(example ?? t("roles_dashboard_scope_example")) && (
            <p className="grey-t">{example ?? t("roles_dashboard_scope_example")}</p>
          )}

          {/** Hauteur minimale : la modale ne saute pas quand on change de source
              ou quand le loader remplace les filtres. */}
          <div style={{ minHeight: 120 }}>
            <PageLoader status={status === "loading" ? STATUS_LOADING : STATUS_LOADED}>
              {status === "error" ? (
                <Button onClick={() => setAttempt((value) => value + 1)}>{t("roles_retry")}</Button>
              ) : isOrdered ? (
                <>
                  {/** Les filtres choisis, numérotés dans l'ordre, puis les autres */}
                  <DragDropContext onDragEnd={reorder}>
                    <Droppable droppableId="ordered-filters" direction="horizontal">
                      {(provided) => (
                        <div className="flex" ref={provided.innerRef} {...provided.droppableProps}>
                          {selected.map((name, index) => (
                            <Draggable key={name} draggableId={name} index={index}>
                              {(provided) => (
                                <div ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps}>
                                  {renderItem(name, index + 1)}
                                </div>
                              )}
                            </Draggable>
                          ))}
                          {provided.placeholder}
                        </div>
                      )}
                    </Droppable>
                  </DragDropContext>
                  <div className="flex flex-wrap">
                    {names.filter((name) => !selected.includes(name)).map((name) => renderItem(name))}
                  </div>
                </>
              ) : (
                <div className="flex flex-wrap">
                  {Array.from(new Set([...selected, ...names])).map((name) => renderItem(name))}
                </div>
              )}
            </PageLoader>
          </div>
        </div>

        {!isOrdered && (
          <div className="flex-none">
            <Dropdown
              title={t("filters")}
              displayField="name"
              active={surveyId}
              value={sources.find((source) => source.id === surveyId)?.name ?? ""}
              list={sources}
              isDisabled={saving || surveysStatus === "loading"}
              onSelect={(source: { id: string }) => {
                if (source.id !== surveyId) {
                  setStatus("loading")
                  setSurveyId(source.id)
                }
              }}
            />
            {surveysStatus === "error" && (
              <Button onClick={() => setAttempt((value) => value + 1)}>{t("roles_retry")}</Button>
            )}
          </div>
        )}
      </div>

    </Modal>
  )
}
